// FILE: src/components/CurrencySelectorDialog.jsx
import React, { useState } from "react";
import { Dialog, DialogContent, DialogActions } from "@mui/material";
import API from "../api";

// Define the currencies available
const currencies = [
  { code: "USD", symbol: "$" },
  { code: "EUR", symbol: "€" },
  { code: "GBP", symbol: "£" },
  { code: "JPY", symbol: "¥" },
  { code: "BDT", symbol: "৳" },
  { code: "INR", symbol: "₹" },
  { code: "USD", symbol: "$" },
  { code: "EUR", symbol: "€" },
  { code: "GBP", symbol: "£" },
  { code: "JPY", symbol: "¥" },
  { code: "BDT", symbol: "৳" },
  { code: "INR", symbol: "₹" },
  { code: "AUD", symbol: "A$" },
  { code: "CAD", symbol: "C$" },
  { code: "CHF", symbol: "CHF" },
  { code: "CNY", symbol: "¥" },
  { code: "HKD", symbol: "HK$" },
  { code: "NZD", symbol: "NZ$" },
  { code: "SEK", symbol: "kr" },
  { code: "NOK", symbol: "kr" },
  { code: "RUB", symbol: "₽" },
  { code: "KRW", symbol: "₩" },
  { code: "MXN", symbol: "$" },
  { code: "ZAR", symbol: "R" },
  { code: "TRY", symbol: "₺" },
  { code: "BRL", symbol: "R$" },
  { code: "SAR", symbol: "﷼" },
  { code: "AED", symbol: "د.إ" },
  { code: "ILS", symbol: "₪" },
  { code: "PLN", symbol: "zł" },
  { code: "THB", symbol: "฿" }
];

// Display-only labels (presentation): full currency names shown next to codes.
const CURRENCY_NAMES = {
  USD: "US Dollar",
  EUR: "Euro",
  GBP: "British Pound",
  JPY: "Japanese Yen",
  BDT: "Bangladeshi Taka",
  INR: "Indian Rupee",
  AUD: "Australian Dollar",
  CAD: "Canadian Dollar",
  CHF: "Swiss Franc",
  CNY: "Chinese Yuan",
  HKD: "Hong Kong Dollar",
  NZD: "New Zealand Dollar",
  SEK: "Swedish Krona",
  NOK: "Norwegian Krone",
  RUB: "Russian Ruble",
  KRW: "South Korean Won",
  MXN: "Mexican Peso",
  ZAR: "South African Rand",
  TRY: "Turkish Lira",
  BRL: "Brazilian Real",
  SAR: "Saudi Riyal",
  AED: "UAE Dirham",
  ILS: "Israeli Shekel",
  PLN: "Polish Zloty",
  THB: "Thai Baht",
};

// De-duplicated view of the same currency set (display only — the source
// array above is unchanged; first occurrence of each code wins).
const displayCurrencies = currencies.filter(
  (cur, idx) => currencies.findIndex((c) => c.code === cur.code) === idx
);

// ----------------------------------------
// CSS — fully scoped under `.curd-root`.
// ----------------------------------------
const CURD_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@400;450;500;600;700&family=Geist+Mono:wght@400;500;600&display=swap');

.curd-root {
  --curd-primary: #1E90FF;
  --curd-primary-soft: #E8F3FF;
  --curd-warn: #FFC107;
  --curd-warn-soft: #FFF7DC;
  --curd-warn-deep: #B86E00;
  --curd-accent: #FFCE1F;
  --curd-success: #2E7D32;
  --curd-success-soft: #E8F5E9;
  --curd-danger: #C62828;
  --curd-danger-soft: #FDECEC;
  --curd-text-1: #111827;
  --curd-text-2: #374151;
  --curd-text-3: #6B7280;
  --curd-text-4: #9CA3AF;
  --curd-border-1: #ECECEC;
  --curd-border-2: #DDDDDD;
  --curd-surface: #FFFFFF;
  --curd-surface-sub: #FAFAFA;
  --curd-surface-sub-2: #F4F4F5;
  --curd-radius-sm: 6px;
  --curd-radius-md: 8px;
  --curd-radius-lg: 12px;
  --curd-shadow-sm: 0 1px 2px rgba(15, 23, 42, 0.04);
  --curd-shadow-md: 0 4px 16px rgba(15, 23, 42, 0.06);
  --curd-navbar-h: 60px;

  font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-feature-settings: 'cv11', 'ss03';
  color: var(--curd-text-1);
  font-size: 13.5px;
  line-height: 1.5;
  letter-spacing: -0.005em;
}
.curd-root * { box-sizing: border-box; }

/* ----- Dialog skin (MUI Dialog underneath, custom look) ----- */
.curd-dlg .MuiPaper-root {
  font-family: 'Geist', -apple-system, BlinkMacSystemFont, sans-serif !important;
  border-radius: 16px !important;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.18) !important;
  overflow: hidden;
}
.curd-dlg-head {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--curd-border-1);
}
.curd-dlg-title { font-size: 16px; font-weight: 600; color: var(--curd-text-1); margin: 0; }
.curd-dlg-sub { font-size: 12px; color: var(--curd-text-3); margin-top: 3px; }
.curd-dlg-close {
  background: none; border: none; color: var(--curd-text-3);
  width: 28px; height: 28px; border-radius: 6px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.curd-dlg-close:hover { background: var(--curd-surface-sub-2); color: var(--curd-text-1); }

.curd-dlg-body { padding: 16px 20px 18px; }

.curd-dlg-foot {
  padding: 14px 20px;
  border-top: 1px solid var(--curd-border-1);
  background: var(--curd-surface-sub);
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px;
}
.curd-dlg-foot-l { font-size: 12px; color: var(--curd-text-3); }
.curd-dlg-foot-r { display: flex; gap: 8px; }

/* ----- Buttons ----- */
.curd-btn {
  display: inline-flex; align-items: center; gap: 7px;
  height: 34px; padding: 0 14px;
  font-family: inherit; font-size: 12.5px; font-weight: 500;
  border-radius: var(--curd-radius-md);
  border: 1px solid transparent; cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.curd-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.curd-btn-primary { background: var(--curd-primary); color: #fff; }
.curd-btn-primary:hover:not(:disabled) { background: #1A7DD9; }
.curd-btn-ghost {
  background: var(--curd-surface); color: var(--curd-text-2);
  border-color: var(--curd-border-2);
}
.curd-btn-ghost:hover:not(:disabled) { background: var(--curd-surface-sub-2); }

/* ----- Section label ----- */
.curd-seclab {
  font-size: 10.5px; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.06em; color: var(--curd-text-3);
  margin: 0 0 8px;
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
}
.curd-seclab-count {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 10.5px; font-weight: 500; color: var(--curd-text-4);
  text-transform: none; letter-spacing: 0;
}

/* ----- Currency option grid ----- */
.curd-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
  gap: 8px;
  max-height: 336px;
  overflow-y: auto;
  padding: 2px;
  margin: 0 -2px;
}
.curd-opt {
  position: relative;
  display: flex; align-items: center; gap: 10px;
  width: 100%; padding: 9px 10px;
  background: var(--curd-surface);
  border: 1px solid #E5E7EB;
  border-radius: var(--curd-radius-lg);
  font: inherit; text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}
.curd-opt:hover { border-color: var(--curd-accent); background: #FFFDF4; }
.curd-opt:focus-visible {
  outline: none;
  border-color: var(--curd-primary);
  box-shadow: 0 0 0 3px var(--curd-primary-soft);
}
.curd-opt-sym {
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; flex-shrink: 0;
  background: var(--curd-surface-sub-2);
  border-radius: var(--curd-radius-md);
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 12.5px; font-weight: 600; color: var(--curd-text-2);
}
.curd-opt-meta { min-width: 0; display: flex; flex-direction: column; gap: 1px; }
.curd-opt-code {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 12.5px; font-weight: 600; color: var(--curd-text-1);
  display: flex; align-items: center; gap: 6px;
}
.curd-opt-name {
  font-size: 11px; color: var(--curd-text-3);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

/* Selected option — yellow accent, like the profile-menu rows */
.curd-opt-selected,
.curd-opt-selected:hover {
  border-color: var(--curd-accent);
  background: #FFFBEB;
  box-shadow: inset 0 0 0 1px var(--curd-accent);
}
.curd-opt-selected .curd-opt-sym {
  background: var(--curd-warn-soft);
  color: var(--curd-warn-deep);
}
.curd-opt-check {
  position: absolute; top: 8px; right: 8px;
  display: flex; align-items: center; justify-content: center;
  width: 16px; height: 16px; border-radius: 50%;
  background: var(--curd-accent); color: #1F1A0A;
}

/* "Current" pill on the saved currency */
.curd-current-pill {
  display: inline-flex; align-items: center;
  padding: 1px 6px; border-radius: 999px;
  background: var(--curd-success-soft); color: var(--curd-success);
  font-family: 'Geist', sans-serif;
  font-size: 9.5px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.04em;
}

@media (max-width: 480px) {
  .curd-grid { grid-template-columns: 1fr 1fr; }
}
`;

// ----- Small inline icons -----
function IconClose({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function IconCheck({ size = 10 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M3 8.5l3.2 3.2L13 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function CurrencySelectorDialog({
  open,
  onClose,
  currentCurrency,
  onCurrencyChange,
}) {
  const [selectedCurrency, setSelectedCurrency] = useState(currentCurrency);

  const handleSave = async () => {
    try {
      // Call the update profile API with the new currency
      await API.put("/users/update-profile", { currency: selectedCurrency });
      // Inform user of the successful update
      alert(`Currency updated to ${selectedCurrency}. The page will now refresh to apply the changes.`);
      // Pass the new currency back to the parent component (NavBar)
      onCurrencyChange(selectedCurrency);
      // Refresh the whole page after updating currency
      window.location.reload();
    } catch (error) {
      console.error("Error updating currency:", error);
      alert("An error occurred while updating the currency. Please try again later.");
    }
  };

  const handleCurrencySelect = (code) => {
    setSelectedCurrency(code);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      className="curd-dlg"
      PaperProps={{ className: "curd-root" }}
    >
      <style>{CURD_CSS}</style>

      <div className="curd-dlg-head">
        <div>
          <div className="curd-dlg-title">Change Currency Preference</div>
          <div className="curd-dlg-sub">
            Applied across all monetary values displayed in the app.
          </div>
        </div>
        <button className="curd-dlg-close" onClick={onClose} aria-label="Close">
          <IconClose />
        </button>
      </div>

      <DialogContent dividers className="curd-dlg-body">
        <div className="curd-seclab">
          <span>Select currency</span>
          <span className="curd-seclab-count">{displayCurrencies.length} available</span>
        </div>
        <div className="curd-grid" role="listbox" aria-label="Currency">
          {displayCurrencies.map((cur) => {
            const isSelected = selectedCurrency === cur.code;
            const isCurrent = currentCurrency === cur.code;
            return (
              <button
                key={cur.code}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`curd-opt${isSelected ? " curd-opt-selected" : ""}`}
                onClick={() => handleCurrencySelect(cur.code)}
              >
                <span className="curd-opt-sym">{cur.symbol}</span>
                <span className="curd-opt-meta">
                  <span className="curd-opt-code">
                    {cur.code}
                    {isCurrent && <span className="curd-current-pill">Current</span>}
                  </span>
                  <span className="curd-opt-name">
                    {CURRENCY_NAMES[cur.code] || cur.code}
                  </span>
                </span>
                {isSelected && (
                  <span className="curd-opt-check">
                    <IconCheck />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </DialogContent>

      <DialogActions className="curd-dlg-foot">
        <div className="curd-dlg-foot-l">
          Saving refreshes the page to apply the change.
        </div>
        <div className="curd-dlg-foot-r">
          <button className="curd-btn curd-btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="curd-btn curd-btn-primary" onClick={handleSave}>
            Save Changes
          </button>
        </div>
      </DialogActions>
    </Dialog>
  );
}
